import { type FormEvent, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { DialogField, Modal } from '@/components/ui/Modal';
import type { RoleSummary } from '@/api/roles.api';
import type { CreateUserRequest, UserDetail } from '@/api/users.api';

export function AddUserDialog({ open, roles, onClose, onCreate }: { open: boolean; roles: RoleSummary[]; onClose: () => void; onCreate: (body: CreateUserRequest) => Promise<UserDetail | void> }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const internalRoles = roles.filter((role) => role.code.toUpperCase() !== 'VENDOR_USER');
  const close = () => { setError(''); setTemporaryPassword(''); onClose(); };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (temporaryPassword) { close(); return; }
    const data = new FormData(event.currentTarget);
    const roleId = String(data.get('roleId'));
    if (!internalRoles.some((role) => role.id === roleId)) {
      setError('Select a valid internal role.');
      return;
    }
    setPending(true); setError('');
    try {
      const created = await onCreate({
        username: String(data.get('username')),
        email: String(data.get('email')),
        fullName: String(data.get('fullName')),
        phone: String(data.get('phone') ?? ''),
        userType: 'INTERNAL',
        roleIds: [roleId],
      });
      if (created?.temporaryPassword) setTemporaryPassword(created.temporaryPassword);
      else close();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to create user.');
    } finally { setPending(false); }
  };
  return <Modal open={open} onClose={close} onSubmit={submit} className="add-user-dialog" size="lg" title="Add user" description="Create an internal account and assign its initial access role." submitLabel={temporaryPassword ? 'Done' : pending ? 'Creating…' : 'Create user'} submitIcon={<UserPlus size={16} />}>
    <div className="dialog-form-grid">
      {error && <div className="form-error full" role="alert">{error}</div>}
      {temporaryPassword && <div className="success-note full"><strong>User created</strong><span>Temporary password: <code>{temporaryPassword}</code></span><small>Copy it now and share it through an approved secure channel.</small></div>}
      {!temporaryPassword && <>
      <DialogField label="Full name" required><input name="fullName" required autoFocus placeholder="Employee’s full name" /></DialogField>
      <DialogField label="Username" required><input name="username" required placeholder="e.g. test.user1" /></DialogField>
      <DialogField label="Work email" required><input name="email" type="email" required placeholder="name@company.com" /></DialogField>
      <DialogField label="Phone"><input name="phone" type="tel" placeholder="9876543210" /></DialogField>
      <DialogField label="Internal role" required className="full"><select name="roleId" required defaultValue=""><option value="" disabled>Select role</option>{internalRoles.map((role) => <option value={role.id} key={role.id}>{role.name}</option>)}</select></DialogField>
      </>}
    </div>
  </Modal>;
}
